import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Eye, LayoutGrid, Search, Share2, Users } from "lucide-react";
import { ModalConfirmacao } from "@/shared/components/ModalConfirmacao";
import { useIgrejaModulos, useSalvarModulo } from "@/core/hooks/useIgrejaModulos";
import type { ModuloIgreja, ModoDadosModulo } from "@/core/services/igrejaModulosService";
import type { ModuloVisibilidade } from "@/shared/types/database.types";
import { normalizarBusca } from "@/shared/lib/busca";

/**
 * UX-07: a lista de ministérios responsáveis era uma pilha fixa de checkboxes
 * sem busca — não escala com muitos ministérios. Campo de busca por nome
 * (acento-insensível) filtra a lista; a seleção em si continua por checkbox.
 */
function SeletorMinisteriosResponsaveis({
  ministerios,
  selecionados,
  disabled,
  onToggle,
}: {
  ministerios: Array<{ id: string; nome: string }>;
  selecionados: string[];
  disabled: boolean;
  onToggle: (ministerioId: string) => void;
}) {
  const [busca, setBusca] = useState("");
  const filtrados = busca.trim()
    ? ministerios.filter((min) => normalizarBusca(min.nome).includes(normalizarBusca(busca)))
    : ministerios;

  return (
    <div className="flex flex-col gap-1 rounded-md border border-border bg-surface-2 p-2">
      {ministerios.length > 5 && (
        <div className="relative mb-1">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted" />
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar ministério..."
            className="w-full rounded-md border border-border bg-surface py-1.5 pl-7 pr-2 text-xs text-text placeholder:text-muted focus:border-primary focus:outline-none"
          />
        </div>
      )}
      {/* Rolagem própria na lista: com muitos ministérios a página ficava enorme
          (a busca acima continua fixa). */}
      <div className="flex max-h-64 flex-col gap-1 overflow-y-auto pr-1">
        {!ministerios.length ? (
          <p className="px-2 py-1 text-xs text-muted">Nenhum ministério ativo cadastrado.</p>
        ) : !filtrados.length ? (
          <p className="px-2 py-1 text-xs text-muted">Nenhum ministério encontrado para "{busca}".</p>
        ) : (
          filtrados.map((min) => (
            <label
              key={min.id}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-text hover:bg-surface"
            >
              <input
                type="checkbox"
                className="h-4 w-4 accent-primary"
                checked={selecionados.includes(min.id)}
                disabled={disabled}
                onChange={() => onToggle(min.id)}
              />
              {min.nome}
            </label>
          ))
        )}
      </div>
    </div>
  );
}

/**
 * Módulos (Núcleo, só admin — 03-Núcleo §3): o que a igreja ativa, quem
 * enxerga cada módulo no menu (visibilidade, DA-002) e qual ministério
 * responde por ele (base do Nível 2 de permissões).
 */
export function GerenciarModulos() {
  const { data, isLoading, isError } = useIgrejaModulos();
  const salvar = useSalvarModulo();
  const navigate = useNavigate();

  const [mensagem, setMensagem] = useState<{ tipo: "sucesso" | "erro"; texto: string } | null>(null);
  const [desativacaoPendente, setDesativacaoPendente] = useState<ModuloIgreja | null>(null);
  const [retiradaPendente, setRetiradaPendente] = useState<{
    modulo: ModuloIgreja;
    ministerioId: string;
    nome: string;
  } | null>(null);

  function exibir(tipo: "sucesso" | "erro", texto: string) {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem(null), 3500);
  }

  async function aplicar(modulo: ModuloIgreja, mudancas: Partial<{
    ativo: boolean;
    visibilidade: ModuloVisibilidade;
    ministerioIds: string[];
    modoDados: ModoDadosModulo;
  }>) {
    try {
      await salvar.mutateAsync({
        codigo: modulo.codigo,
        cfg: {
          ativo: mudancas.ativo ?? modulo.ativo,
          visibilidade: mudancas.visibilidade ?? modulo.visibilidade,
          ministerioIds:
            mudancas.ministerioIds !== undefined ? mudancas.ministerioIds : modulo.ministerioIds,
          modoDados: mudancas.modoDados ?? modulo.modoDados,
        },
      });
      exibir("sucesso", `Configuração de ${modulo.nome} salva.`);
    } catch (e) {
      exibir("erro", e instanceof Error ? e.message : "Não foi possível salvar.");
    }
  }

  function alternarMinisterio(modulo: ModuloIgreja, ministerioId: string) {
    const jaSelecionado = modulo.ministerioIds.includes(ministerioId);
    if (jaSelecionado) {
      // Retirar o ministério responsável tira a gestão do elo de quem a tinha por ele: confirma antes.
      // (`ministerioNomes` vem ordenado por nome, não na ordem dos ids — por isso busca pelo id.)
      setRetiradaPendente({
        modulo,
        ministerioId,
        nome: data?.ministerios.find((min) => min.id === ministerioId)?.nome ?? "este ministério",
      });
      return;
    }
    aplicarAlternancia(modulo, ministerioId);
  }

  function aplicarAlternancia(modulo: ModuloIgreja, ministerioId: string) {
    const jaSelecionado = modulo.ministerioIds.includes(ministerioId);
    const novos = jaSelecionado
      ? modulo.ministerioIds.filter((id) => id !== ministerioId)
      : [...modulo.ministerioIds, ministerioId];
    // F6-14: "Individual" só faz sentido com 2+ ministérios responsáveis. Ao
    // cair para 0 ou 1, volta o modo para "Compartilhado" — senão o valor fica
    // preso (o seletor some da tela e não há como reverter).
    aplicar(
      modulo,
      novos.length <= 1
        ? { ministerioIds: novos, modoDados: "compartilhado" }
        : { ministerioIds: novos },
    );
  }

  function definirModoDados(modulo: ModuloIgreja, modo: ModoDadosModulo) {
    aplicar(modulo, { modoDados: modo });
  }

  async function confirmarRetirada() {
    if (!retiradaPendente) return;
    const { modulo, ministerioId } = retiradaPendente;
    setRetiradaPendente(null);
    aplicarAlternancia(modulo, ministerioId);
  }

  function handleToggleAtivo(modulo: ModuloIgreja) {
    if (modulo.ativo) {
      setDesativacaoPendente(modulo);
    } else {
      aplicar(modulo, { ativo: true });
    }
  }

  async function confirmarDesativacao() {
    if (!desativacaoPendente) return;
    const modulo = desativacaoPendente;
    setDesativacaoPendente(null);
    await aplicar(modulo, { ativo: false });
  }

  return (
    <>
      <main className="mx-auto w-full max-w-4xl px-4 pb-12 pt-8 sm:px-6 sm:pt-10">
        <header className="mb-6">
          <h1 className="text-2xl font-black tracking-tight text-primary sm:text-3xl">Elos da igreja</h1>
          <p className="mt-1 text-sm text-muted">
            Ative os elos da igreja, defina quem os enxerga no menu e o ministério responsável
            pela gestão. Visibilidade não é permissão: quem gerencia se define dentro do
            ministério responsável, pelas funções com o marcador "Gestor de elo".
          </p>
        </header>

        {mensagem && (
          <p
            role="alert"
            className={`mb-4 rounded-xl px-4 py-3 text-sm font-medium ${
              mensagem.tipo === "sucesso" ? "bg-success/10 text-success" : "bg-danger/10 text-danger"
            }`}
          >
            {mensagem.texto}
          </p>
        )}

        {isLoading ? (
          <p className="text-sm text-muted">Carregando...</p>
        ) : isError || !data ? (
          <p role="alert" className="rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger">
            Não foi possível carregar os elos. Tente novamente.
          </p>
        ) : !data.modulos.length ? (
          <p className="rounded-xl border border-border bg-surface p-6 text-sm text-muted">
            O catálogo de elos está vazio.
          </p>
        ) : (
          <div className="space-y-4">
            {data.modulos.map((m) => (
              <section key={m.codigo} className="rounded-2xl border border-border bg-surface p-5">
                <div className="flex items-start gap-4">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                    <LayoutGrid size={22} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-base font-bold text-text">{m.nome}</h2>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                          m.ativo ? "bg-success/10 text-success" : "bg-surface-2 text-muted"
                        }`}
                      >
                        {m.ativo ? "Ativo" : "Desativado"}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-muted">{m.descricao ?? ""}</p>
                  </div>
                  {/* Toggle ativo/desativado */}
                  <button
                    type="button"
                    role="switch"
                    aria-checked={m.ativo}
                    aria-label={m.ativo ? `Desativar ${m.nome}` : `Ativar ${m.nome}`}
                    disabled={salvar.isPending}
                    onClick={() => handleToggleAtivo(m)}
                    className="flex h-11 w-11 shrink-0 items-center justify-center disabled:opacity-60"
                  >
                    <span
                      aria-hidden="true"
                      className={`relative h-6 w-11 rounded-full transition-colors ${
                        m.ativo ? "bg-primary" : "bg-border"
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 h-5 w-5 rounded-full bg-surface transition-transform ${
                          m.ativo ? "left-0.5 translate-x-5" : "left-0.5"
                        }`}
                      />
                    </span>
                  </button>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor={`visibilidade-${m.codigo}`}
                      className="flex items-center gap-1.5 text-sm font-medium text-text"
                    >
                      <Eye size={14} className="text-muted" />
                      Quem vê no menu
                    </label>
                    <select
                      id={`visibilidade-${m.codigo}`}
                      value={m.visibilidade}
                      disabled={salvar.isPending}
                      onChange={(e) =>
                        aplicar(m, { visibilidade: e.target.value as ModuloVisibilidade })
                      }
                      className="rounded-md border border-transparent bg-surface-2 px-4 py-2.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
                    >
                      <option value="todos_membros">Todos os membros</option>
                      <option value="participantes_ministerio">
                        Só integrantes do ministério responsável
                      </option>
                      <option value="lideres_gestores">
                        Só pessoas com funções de líder e gestores
                      </option>
                      <option value="integrantes_qualquer_ministerio">
                        Qualquer integrante ativo de algum ministério
                      </option>
                    </select>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <span className="flex items-center gap-1.5 text-sm font-medium text-text">
                      <Users size={14} className="text-muted" />
                      Ministérios responsáveis <span className="text-muted">(pode marcar mais de um)</span>
                    </span>
                    <SeletorMinisteriosResponsaveis
                      ministerios={data.ministerios}
                      selecionados={m.ministerioIds}
                      disabled={salvar.isPending}
                      onToggle={(ministerioId) => alternarMinisterio(m, ministerioId)}
                    />
                    {m.ministerioIds.length === 1 && (
                      <button
                        type="button"
                        onClick={() => navigate(`/admin/ministerios/${m.ministerioIds[0]}`)}
                        className="inline-flex items-center gap-1 self-start text-xs font-bold text-primary hover:underline"
                      >
                        Gerenciar no ministério
                        <ArrowUpRight size={12} />
                      </button>
                    )}

                    {m.ministerioIds.length > 1 && (
                      <div className="mt-1 flex flex-col gap-1.5 rounded-md border border-primary/30 bg-primary/5 p-2.5">
                        <span className="flex items-center gap-1.5 text-xs font-semibold text-text">
                          <Share2 size={13} className="text-primary" />
                          Como as informações são vistas entre esses ministérios?
                        </span>
                        <div className="flex gap-1 rounded-md bg-surface p-1">
                          <button
                            type="button"
                            disabled={salvar.isPending}
                            onClick={() => definirModoDados(m, "compartilhado")}
                            className={`flex-1 rounded px-2 py-1.5 text-xs font-semibold transition-colors disabled:opacity-60 ${
                              m.modoDados === "compartilhado"
                                ? "bg-primary text-on-primary"
                                : "text-muted hover:bg-surface-2"
                            }`}
                          >
                            Compartilhado
                          </button>
                          <button
                            type="button"
                            disabled={salvar.isPending}
                            onClick={() => definirModoDados(m, "individual")}
                            className={`flex-1 rounded px-2 py-1.5 text-xs font-semibold transition-colors disabled:opacity-60 ${
                              m.modoDados === "individual"
                                ? "bg-primary text-on-primary"
                                : "text-muted hover:bg-surface-2"
                            }`}
                          >
                            Individual
                          </button>
                        </div>
                        {/* F6-14 (revisão 21/07/2026, migration 0048): "Individual" agora
                            tem efeito real em Eventos e Reservas — cada ministério só
                            gerencia (e, em Reservas, só enxerga na tela de gestão) os itens
                            vinculados a ele. Biblioteca fica de fora: livro não tem
                            ministério dono, não há o que separar. */}
                        {m.modoDados === "compartilhado" ? (
                          <p className="text-[11px] leading-snug text-muted">
                            Todos os ministérios marcados acima veem e gerenciam as mesmas informações do
                            elo.
                          </p>
                        ) : m.codigo === "biblioteca" ? (
                          <p className="text-[11px] leading-snug text-warning">
                            Biblioteca não separa por ministério — livro não pertence a nenhum
                            ministério específico. A preferência fica salva, mas na prática
                            continua se comportando como "Compartilhado" aqui.
                          </p>
                        ) : (
                          <p className="text-[11px] leading-snug text-text">
                            Cada ministério marcado acima só gerencia os itens vinculados a ele
                            mesmo — inclusive na listagem de quem pode ver/editar. Um item sem
                            nenhum desses ministérios vinculado (ex.: criado pela administração da igreja) continua
                            visível a quem tiver outro motivo de acesso (líder do próprio
                            ministério do item, admin, etc.).
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {m.ativo && !m.ministerioIds.length && (
                  <p className="mt-3 rounded-md bg-warning/10 px-3 py-2 text-xs leading-relaxed text-warning">
                    Sem ministério responsável, ninguém pode receber a gestão deste elo — só o
                    fluxo de membro comum funciona.
                  </p>
                )}
              </section>
            ))}
          </div>
        )}
      </main>

      {retiradaPendente && (
        <ModalConfirmacao
          aberto={!!retiradaPendente}
          titulo="Remover ministério responsável"
          mensagem={`Remover "${retiradaPendente.nome}" como ministério responsável por "${retiradaPendente.modulo.nome}"? Quem gerencia este elo por esse ministério deixa de gerenciá-lo.${
            retiradaPendente.modulo.ministerioIds.length === 1
              ? " Sem ministério responsável, ninguém poderá receber a gestão do elo."
              : ""
          }`}
          textoBotaoConfirmar="Remover"
          variante="perigo"
          carregando={salvar.isPending}
          onConfirmar={confirmarRetirada}
          onCancelar={() => setRetiradaPendente(null)}
        />
      )}

      {desativacaoPendente && (
        <ModalConfirmacao
          aberto={!!desativacaoPendente}
          titulo="Desativar elo"
          mensagem={`Desativar "${desativacaoPendente.nome}"? Ele some do menu de TODOS os usuários imediatamente. As informações do elo são preservadas e voltam quando reativar.`}
          textoBotaoConfirmar="Desativar"
          variante="perigo"
          carregando={salvar.isPending}
          onConfirmar={confirmarDesativacao}
          onCancelar={() => setDesativacaoPendente(null)}
        />
      )}
    </>
  );
}
